import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueries, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import type { Run, NodeExecution, RunFilters } from '@/types/monitoring';
import { useMonitoringStore } from '@/stores/monitoringStore';
import { isAgentExecution } from '@/utils/monitoring';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/auth/useAuth';
import { telemetryService } from '@/services/telemetryService';
import {
  fetchRuns as fetchRunsApi,
  fetchRunsPage,
  fetchRun as fetchRunApi,
  fetchActionDetails,
  fetchTriggerDetails,
  triggerWorkflow,
  triggerDraftWorkflow,
  fetchTriggerCallbackUrl,
  fetchWorkflowTriggers,
  fetchActionRepetitions,
  fetchChildActionRepetitions,
  fetchMoreRepetitions,
  fetchRepetitionDetails,
  fetchRepetitionScopeActions,
  fetchAgentRepetitions,
  fetchAgentRepetitionActions,
  fetchRunTimeline,
  fetchRunChatHistory,
  fetchActionChatHistory,
  fetchChatMessageIterationActions,
  fetchChatMessageIterationActionDetails,
  resubmitRun,
  cancelRun,
  type TriggerInfo,
  type TriggerPayload,
  type RunsPage,
  type RepetitionsPage,
  type RepetitionInfo,
  type AgentIterationInfo,
  type AgentToolActionInfo,
  type ChatMessageEntry,
  type ChatMessageIterationAction,
  type BuiltInToolIterationInfo,
  triggerMockWorkflowRun,
} from '@/services/monitoringApi';
import { useWorkflowStore } from '@/stores/workflowStore';
import type { MockNodeInfo, TriggerMockPayload } from '@/types/templateMode';

// ============================================================================
// Constants
// ============================================================================

/** Prefix for trigger node IDs in the workflow */
export const NODE_ID_PREFIX_TRIGGER = 'trigger-';

/** Prefix for action node IDs in the workflow */
export const NODE_ID_PREFIX_ACTION = 'action-';

/** Prefix for tool node IDs in the workflow (used in monitoring mode) */
export const NODE_ID_PREFIX_TOOL = 'tool-';

/** Prefix for chatMessage iteration action node IDs (format: chatmsg-action-{iterationIndex}-{actionName}) */
export const NODE_ID_PREFIX_CHATMSG_ACTION = 'chatmsg-action-';

/**
 * Built-in tools that are provided by the AI model/runtime, not workflow actions.
 * These don't have action details available via the workflow API.
 */
const BUILT_IN_TOOLS = new Set(['code_interpreter', 'file_search']);

/** Polling interval when runs are actively executing (5 seconds) */
export const ACTIVE_RUN_POLL_INTERVAL = 5000;

/**
 * Query-key prefixes for queries scoped to a single run (runId at index 1 of the
 * key tuple). Single source of truth — when a new run-scoped hook is added below,
 * its key prefix MUST be added here so toolbar Refresh keeps invalidating it.
 *
 * Used by `WorkflowDesignerWithCode` to invalidate exactly the run-scoped queries
 * without false positives from substring or unrelated key matches.
 */
export const RUN_SCOPED_QUERY_KEY_PREFIXES: ReadonlySet<string> = new Set([
  'run',
  'actionDetails',
  'actionRepetitions',
  'childActionRepetitions',
  'repetitionDetails',
  'repetitionScopeActions',
  'agentIterations',
  'agentIterationActions',
  'statefulAgentIterations',
  'runTimeline',
  'chatHistory',
  'actionChatHistory',
  'chatMessageIterationActions',
  'chatMessageIterationNodeExecutions',
]);

/** Polling interval for monitoring a single run (3 seconds) */
const SINGLE_RUN_POLL_INTERVAL = 3000;

/**
 * staleTime for the runs list queries. Kept short so switching from the
 * designer tab back to monitoring refetches near-instantly instead of
 * showing a cached (possibly empty) list.
 */
const RUNS_LIST_STALE_TIME = 5000;

/** Maximum retry attempts for token acquisition */
const MAX_TOKEN_RETRIES = 2;

/** Delay between token retry attempts (ms) */
const TOKEN_RETRY_DELAY = 1000;

// ============================================================================
// Polling helpers (exported for testing)
// ============================================================================

/** Compute the refetchInterval for the useRuns query */
export function getRunsRefetchInterval(
  data: Run[] | undefined,
  autoRefreshEnabled: boolean,
  autoRefreshInterval: number,
): number | false {
  const hasActiveRun = data?.some((run) => run.status === 'running');
  if (hasActiveRun) return ACTIVE_RUN_POLL_INTERVAL;
  return autoRefreshEnabled ? autoRefreshInterval : false;
}

/** Compute the refetchInterval for the useInfiniteRuns query */
export function getInfiniteRunsRefetchInterval(
  pages: { runs: Run[] }[] | undefined,
  autoRefreshEnabled: boolean,
  autoRefreshInterval: number,
): number | false {
  const allRuns = pages?.flatMap((page) => page.runs) ?? [];
  const hasActiveRun = allRuns.some((run) => run.status === 'running');
  if (hasActiveRun) return ACTIVE_RUN_POLL_INTERVAL;
  return autoRefreshEnabled ? autoRefreshInterval : false;
}

// ============================================================================
// Utilities
// ============================================================================

/**
 * Filter runs based on filter criteria
 */
export const filterRuns = (runs: Run[], filters: RunFilters): Run[] => {
  return runs.filter((run) => {
    // Status filter
    if (filters.statuses.length > 0 && !filters.statuses.includes(run.status)) {
      return false;
    }

    // Workflow name filter
    if (filters.workflowName && run.workflowName !== filters.workflowName) {
      return false;
    }

    // Date range filter
    if (filters.dateRange) {
      const runDate = new Date(run.startTime);
      if (filters.dateRange.start) {
        const startDate = new Date(filters.dateRange.start);
        if (runDate < startDate) return false;
      }
      if (filters.dateRange.end) {
        const endDate = new Date(filters.dateRange.end);
        if (runDate > endDate) return false;
      }
    }

    // Duration range filter (in milliseconds)
    if (filters.durationRange) {
      if (run.duration === undefined) {
        return false;
      }
      if (filters.durationRange.min !== undefined && run.duration < filters.durationRange.min) {
        return false;
      }
      if (filters.durationRange.max !== undefined && run.duration > filters.durationRange.max) {
        return false;
      }
    }

    // Search query filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      const matchesWorkflowName = run.workflowName.toLowerCase().includes(query);
      const matchesExecutionContent = run.executions?.some(
        (exec) =>
          exec.inputMessage?.toLowerCase().includes(query) ||
          exec.outputMessage?.toLowerCase().includes(query)
      );
      if (!matchesWorkflowName && !matchesExecutionContent) {
        return false;
      }
    }

    // Version filter (draft vs published)
    if (filters.versionFilter) {
      if (filters.versionFilter === 'draft' && !run.isDraftRun) {
        return false;
      }
      if (filters.versionFilter === 'published' && run.isDraftRun) {
        return false;
      }
    }

    return true;
  });
};

/**
 * Sleep utility for retry delays
 */
const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// ============================================================================
// Internal Hooks
// ============================================================================

/**
 * Hook to get API options from context with token acquisition helper
 */
export const useMonitoringApiOptions = () => {
  const { workflowName } = useParams<{ workflowName: string }>();
  const { selectedApp, selectedProject } = useAppStore();
  const { getOttoToken } = useAuth();

  const isReady = useMemo(() => !!selectedApp && !!selectedProject && !!workflowName, [selectedApp, selectedProject, workflowName]);

  /**
   * Acquire token with retry logic for transient failures
   */
  const acquireToken = useCallback(async (): Promise<string> => {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= MAX_TOKEN_RETRIES; attempt++) {
      try {
        const token = await getOttoToken();
        if (token) {
          return token;
        }
        lastError = new Error('Token acquisition returned null');
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Token acquisition failed');
      }

      // Wait before retrying (except on last attempt)
      if (attempt < MAX_TOKEN_RETRIES) {
        await sleep(TOKEN_RETRY_DELAY);
      }
    }

    throw lastError ?? new Error('Failed to acquire token after retries');
  }, [getOttoToken]);

  return {
    workflowName,
    projectName: selectedProject ?? '',
    appName: selectedApp?.name,
    acquireToken,
    isReady,
  };
};

// ============================================================================
// Public Hooks - Run Data
// ============================================================================

/**
 * Hook to get all runs with filtering and smart polling
 */
export const useRuns = () => {
  const filters = useMonitoringStore((state) => state.filters);
  const autoRefreshEnabled = useMonitoringStore((state) => state.autoRefreshEnabled);
  const autoRefreshInterval = useMonitoringStore((state) => state.autoRefreshInterval);
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();

  const query = useQuery({
    queryKey: ['runs', workflowName, projectName, appName],
    queryFn: async (): Promise<Run[]> => {
      // These checks are redundant with enabled, but TypeScript needs them
      if (!projectName || !workflowName || !appName) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();

      return fetchRunsApi({
        projectName,
        appName,
        workflowName,
        accessToken: token,
      });
    },
    enabled: isReady,
    staleTime: RUNS_LIST_STALE_TIME,
    refetchInterval: (query) =>
      getRunsRefetchInterval(query.state.data, autoRefreshEnabled, autoRefreshInterval),
  });

  const filteredRuns = useMemo(() => {
    if (!query.data) return [];
    return filterRuns(query.data, filters);
  }, [query.data, filters]);

  return {
    ...query,
    data: filteredRuns,
    allRuns: query.data ?? [],
  };
};

/**
 * Hook to get runs with infinite scroll pagination
 * @param options.enabled - Override to disable the query (e.g., when the monitoring tab is hidden). Defaults to true.
 */
export const useInfiniteRuns = (options?: { enabled?: boolean }) => {
  const filters = useMonitoringStore((state) => state.filters);
  const autoRefreshEnabled = useMonitoringStore((state) => state.autoRefreshEnabled);
  const autoRefreshInterval = useMonitoringStore((state) => state.autoRefreshInterval);
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();

  const callerEnabled = options?.enabled ?? true;

  const query = useInfiniteQuery({
    queryKey: ['infiniteRuns', workflowName, projectName, appName],
    queryFn: async ({ pageParam }): Promise<RunsPage> => {
      if (!projectName || !workflowName || !appName) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();

      return fetchRunsPage(
        { projectName, appName, workflowName, accessToken: token },
        pageParam
      );
    },
    enabled: isReady && callerEnabled,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextLink,
    staleTime: RUNS_LIST_STALE_TIME,
    refetchInterval: callerEnabled
      ? (query) => getInfiniteRunsRefetchInterval(query.state.data?.pages, autoRefreshEnabled, autoRefreshInterval)
      : false,
  });

  const allRuns = useMemo(() => {
    if (!query.data?.pages) return [];
    return query.data.pages.flatMap((page) => page.runs);
  }, [query.data?.pages]);

  const filteredRuns = useMemo(() => {
    return filterRuns(allRuns, filters);
  }, [allRuns, filters]);

  return {
    ...query,
    data: filteredRuns,
    allRuns,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
};

/**
 * Hook to get a specific run by ID
 */
export const useRun = (runId: string | null) => {
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();

  return useQuery({
    queryKey: ['run', runId, workflowName, projectName, appName],
    queryFn: async (): Promise<Run> => {
      if (!projectName || !workflowName || !appName || !runId) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();

      const run = await fetchRunApi(
        {
          projectName,
          appName,
          workflowName,
          accessToken: token,
        },
        runId
      );

      if (!run) {
        throw new Error(`Run ${runId} not found`);
      }

      telemetryService.trackEvent('execution.run.selected', { status: run.status });
      return run;
    },
    enabled: isReady && !!runId,
    staleTime: 30000,
    refetchInterval: (query) => {
      const run = query.state.data;
      return run?.status === 'running' ? SINGLE_RUN_POLL_INTERVAL : false;
    },
  });
};

/**
 * Hook to get currently selected run data
 */
export const useSelectedRun = () => {
  const selectedRunId = useMonitoringStore((state) => state.selectedRunId);
  return useRun(selectedRunId);
};

// ============================================================================
// Public Hooks - Action Details
// ============================================================================

/**
 * Hook to get action/trigger input/output details
 * @param runId - The run ID
 * @param nodeId - The node ID (trigger-xxx, action-xxx, chatmsg-action-{iterationIndex}-{actionName})
 * @param options - Optional configuration
 * @param options.isAgenticWorkflow - If true, skip trigger details fetch (agentic triggers don't support this API)
 */
export const useActionDetails = (
  runId: string | null,
  nodeId: string | null,
  options?: { isAgenticWorkflow?: boolean }
) => {
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();
  const isAgenticWorkflow = options?.isAgenticWorkflow ?? false;

  // For agentic workflows, the trigger details API returns 404.
  // Skip the query entirely for trigger nodes in agentic workflows.
  const isTriggerNode = nodeId?.startsWith(NODE_ID_PREFIX_TRIGGER) ?? false;
  const isChatMessageAction = nodeId?.startsWith(NODE_ID_PREFIX_CHATMSG_ACTION) ?? false;
  const isToolNode = nodeId?.startsWith(NODE_ID_PREFIX_TOOL) ?? false;
  const shouldSkipForAgenticTrigger = isAgenticWorkflow && isTriggerNode;

  // Check if this is a built-in tool (like code_interpreter) that doesn't have action details
  const isBuiltInTool = (() => {
    if (!isToolNode || !nodeId) return false;
    const withoutPrefix = nodeId.slice(NODE_ID_PREFIX_TOOL.length);
    const lastDashIndex = withoutPrefix.indexOf('-');
    const toolName = lastDashIndex >= 0 ? withoutPrefix.slice(lastDashIndex + 1) : withoutPrefix;
    return BUILT_IN_TOOLS.has(toolName);
  })();

  return useQuery({
    queryKey: ['actionDetails', runId, nodeId, workflowName, projectName, appName],
    queryFn: async (): Promise<{
      input?: unknown;
      output?: unknown;
      agentMetadata?: NodeExecution['agentMetadata'];
      retryHistory?: NodeExecution['retryHistory'];
    }> => {
      if (!projectName || !workflowName || !appName || !runId || !nodeId) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();

      const opts = {
        projectName,
        appName,
        workflowName,
        accessToken: token,
      };

      if (isTriggerNode) {
        const triggerName = nodeId.slice(NODE_ID_PREFIX_TRIGGER.length);
        const result = await fetchTriggerDetails(opts, runId, triggerName);
        return result ?? {};
      } else if (isChatMessageAction) {
        // ChatMessage iteration action format: "chatmsg-action-{iterationIndex}-{actionName}"
        // Example: "chatmsg-action-0-Check_Message_Type" -> iterationIndex=0, actionName="Check_Message_Type"
        const withoutPrefix = nodeId.slice(NODE_ID_PREFIX_CHATMSG_ACTION.length);
        const firstDashIndex = withoutPrefix.indexOf('-');
        if (firstDashIndex < 0) {
          console.error('[useActionDetails] Invalid chatmsg-action format:', nodeId);
          return {};
        }
        const iterationIndex = parseInt(withoutPrefix.slice(0, firstDashIndex), 10);
        const actionName = withoutPrefix.slice(firstDashIndex + 1);
        
        if (isNaN(iterationIndex) || !actionName) {
          console.error('[useActionDetails] Failed to parse chatmsg-action:', nodeId);
          return {};
        }
        
        const result = await fetchChatMessageIterationActionDetails(opts, runId, iterationIndex, actionName);
        return result ?? {};
      } else if (isToolNode) {
        // Tool-node details are fetched via agent repetition APIs and stored in toolActions.
        // Calling /runs/{runId}/actions/{actionName} for tool actions can 404 (WorkflowRunActionNotFound).
        return {};
      } else {
        // Extract action name from different node ID formats:
        // - "action-ActionName" -> "ActionName"
        // - "tool-AgentName-ActionName" -> "ActionName"
        // - "action-ScopeName/ChildName" -> foreach child, use repetitions API
        // - "ActionName" -> "ActionName"
        let actionName: string;
        if (nodeId.startsWith(NODE_ID_PREFIX_TOOL)) {
          // Tool node format: "tool-AgentName-ActionName"
          // Extract the action name (last segment after the agent name)
          const withoutPrefix = nodeId.slice(NODE_ID_PREFIX_TOOL.length);
          const lastDashIndex = withoutPrefix.indexOf('-');
          actionName = lastDashIndex >= 0 ? withoutPrefix.slice(lastDashIndex + 1) : withoutPrefix;
        } else if (nodeId.startsWith(NODE_ID_PREFIX_ACTION)) {
          actionName = nodeId.slice(NODE_ID_PREFIX_ACTION.length);
        } else {
          actionName = nodeId;
        }

        // Foreach child actions have a slash-delimited ID like "ScopeName/ChildName".
        // The runtime does not support this as a flat action name — use the
        // /actions/{childName}/repetitions/{rep} path instead.
        if (actionName.includes('/')) {
          const slashIndex = actionName.indexOf('/');
          const childName = actionName.slice(slashIndex + 1);
          // Fetch the available repetitions for this child action (one per foreach iteration).
          const repetitionsPage = await fetchChildActionRepetitions(opts, runId, childName);
          if (!repetitionsPage.repetitions || repetitionsPage.repetitions.length === 0) {
            return {};
          }
          // Default to the first repetition (iteration 0). In the future this
          // could be driven by a selected iteration index passed through options.
          const repetitionName = repetitionsPage.repetitions[0]?.name;
          if (!repetitionName) return {};
          const scopeName = actionName.slice(0, slashIndex);
          const childResults = await fetchRepetitionScopeActions(opts, runId, scopeName, repetitionName, [childName]);
          const childExec = childResults[0];
          if (!childExec) return {};
          return { input: childExec.input, output: childExec.output };
        }

        const result = await fetchActionDetails(opts, runId, actionName);
        return result ?? {};
      }
    },
    // Disable the query for unsupported node types.
    // Tool-node details already come from agent repetition APIs (toolActions map).
    enabled: isReady && !!runId && !!nodeId && !shouldSkipForAgenticTrigger && !isToolNode && !isBuiltInTool,
    staleTime: 60000,
  });
};

// ============================================================================
// Public Hooks - Workflow Triggers
// ============================================================================

/**
 * Hook to get workflow triggers
 */
export const useWorkflowTriggers = () => {
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();

  return useQuery({
    queryKey: ['workflowTriggers', workflowName, projectName, appName],
    queryFn: async (): Promise<TriggerInfo[]> => {
      if (!projectName || !workflowName || !appName) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();

      return fetchWorkflowTriggers({
        projectName,
        appName,
        workflowName,
        accessToken: token,
      });
    },
    enabled: isReady,
    staleTime: 300000,
  });
};

/**
 * Result shape returned by {@link useTriggerCallbackUrl}.
 *
 * Note: an explicit `isError` field is intentionally omitted. The underlying
 * `fetchTriggerCallbackUrl` collapses every failure into `null` (see its
 * implementation in `monitoringApi.ts`), so React Query's error state is
 * never reachable from this hook. Consumers should treat `url === undefined`
 * as the only meaningful "no URL" signal.
 */
export interface TriggerCallbackUrlResult {
  /** Resolved callback URL, or undefined when not yet available. */
  url: string | undefined;
  /** HTTP method declared by the runtime for the callback (defaults to POST). */
  method: string | undefined;
  /** True while the URL is being fetched for the first time. */
  isLoading: boolean;
}

/**
 * Hook to fetch a single trigger's callback URL on demand.
 *
 * This is a lightweight alternative to {@link useWorkflowTriggers} for callers
 * that only need the URL for one trigger (e.g., the node editor's parameters
 * panel). The query key is namespaced under 'workflowTriggers' so existing
 * invalidations on save/publish also refresh it.
 */
export const useTriggerCallbackUrl = (
  triggerName: string | undefined,
  options: { enabled?: boolean } = {}
): TriggerCallbackUrlResult => {
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();
  const { enabled = true } = options;

  const queryEnabled = isReady && enabled && !!triggerName;

  const query = useQuery({
    queryKey: ['workflowTriggers', workflowName, projectName, appName, 'callback', triggerName],
    queryFn: async () => {
      if (!projectName || !workflowName || !appName || !triggerName) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();

      return fetchTriggerCallbackUrl(
        {
          projectName,
          appName,
          workflowName,
          accessToken: token,
        },
        triggerName
      );
    },
    enabled: queryEnabled,
    // Short stale window: callback URLs are cheap to refetch and we want them
    // to refresh when the user re-opens the trigger node after a publish/save
    // race (the runtime may take a moment to register the URL). Combined with
    // the targeted invalidations on save/publish, this gives natural recovery
    // without polling.
    staleTime: 30_000,
  });

  return {
    url: query.data?.value,
    method: query.data?.method,
    // `isLoading` is true only on the initial fetch when the query is enabled.
    // When disabled (no triggerName, etc.) we report not-loading so consumers
    // can render a stable empty state instead of a perpetual skeleton.
    isLoading: queryEnabled && query.isLoading,
  };
};

/**
 * Hook to trigger a workflow
 * Accepts optional TriggerPayload with method, headers, queries, and body
 * Supports both draft and published modes via isDraftMode flag
 */
export const useTriggerWorkflow = () => {
  const queryClient = useQueryClient();
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();

  return useMutation({
    mutationFn: async ({
      triggerName,
      payload,
      isDraftMode = false,
    }: {
      triggerName: string;
      payload?: TriggerPayload;
      /** If true, uses /runDraft endpoint instead of callback URL */
      isDraftMode?: boolean;
    }): Promise<{ success: boolean; error?: string; runId?: string }> => {
      if (!isReady || !projectName || !workflowName || !appName) {
        return { success: false, error: 'Workflow context not ready' };
      }

      try {
        const token = await acquireToken();
        const opts = {
          projectName,
          appName,
          workflowName,
          accessToken: token,
        };

        if (isDraftMode) {
          // Use draft run endpoint
          return triggerDraftWorkflow(opts, triggerName, payload);
        } else {
          // Use published workflow endpoint (callback URL)
          return triggerWorkflow(opts, triggerName, payload);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        return { success: false, error: message };
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['runs', workflowName] });
        queryClient.invalidateQueries({ queryKey: ['infiniteRuns', workflowName] });
      }
    },
  });
};

export const useTriggerMockRun = () => {
  const queryClient = useQueryClient();
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();
  const mockedNodes = useWorkflowStore((state) => state.mockedNodes);

  return useMutation({
    mutationFn: async (triggerName: string): Promise<{ success: boolean; error?: string; runId?: string }> => {
      if (!isReady || !projectName || !workflowName || !appName) {
        return { success: false, error: 'Workflow context not ready' };
      }

      try {
        const token = await acquireToken();
        const opts = {
          projectName,
          appName,
          workflowName,
          accessToken: token,
        };

        // Canvas-side keys are prefixed (`trigger-<name>`, `action-<name>`,
        // `tool-<agent>-<innerAction>`). The backend's actionOverrides map is
        // keyed by the leaf action name, which we read straight off
        // MockNodeInfo.actionName — that field is populated by the workflow
        // walker (scoped name for actions, inner action name for tools) and
        // is the same key the recipe uses for its mocks.
        const triggerPayload = mockedNodes[`trigger-${triggerName}`] as TriggerMockPayload | undefined;
        const actionMocks: Record<string, MockNodeInfo> = Object.fromEntries(
          Object.entries(mockedNodes)
            .filter(([key]) => key.startsWith('action-') || key.startsWith('tool-'))
            .map(([, value]) => {
              const info = value as MockNodeInfo;
              return [info.actionName, info];
            })
        );
        return triggerMockWorkflowRun(opts, triggerName, triggerPayload, actionMocks);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        return { success: false, error: message };
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['runs', workflowName] });
        queryClient.invalidateQueries({ queryKey: ['infiniteRuns', workflowName] });
      }
    },
  });
};

/**
 * Hook to resubmit (rerun) a workflow run
 * This uses the original trigger inputs to start a new run
 */
export const useResubmitRun = () => {
  const queryClient = useQueryClient();
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();

  return useMutation({
    mutationFn: async ({
      runId,
      triggerName,
    }: {
      runId: string;
      triggerName: string;
    }): Promise<{ success: boolean; error?: string }> => {
      if (!isReady || !projectName || !workflowName || !appName) {
        return { success: false, error: 'Workflow context not ready' };
      }

      try {
        const token = await acquireToken();

        await resubmitRun(
          {
            projectName,
            appName,
            workflowName,
            accessToken: token,
          },
          runId,
          triggerName
        );

        return { success: true };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        return { success: false, error: message };
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['runs', workflowName] });
        queryClient.invalidateQueries({ queryKey: ['infiniteRuns', workflowName] });
      }
    },
  });
};

/**
 * Hook to cancel a running workflow
 */
export const useCancelRun = () => {
  const queryClient = useQueryClient();
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();

  return useMutation({
    mutationFn: async ({
      runId,
    }: {
      runId: string;
    }): Promise<{ success: boolean; error?: string }> => {
      if (!isReady || !projectName || !workflowName || !appName) {
        return { success: false, error: 'Workflow context not ready' };
      }

      try {
        const token = await acquireToken();

        await cancelRun(
          {
            projectName,
            appName,
            workflowName,
            accessToken: token,
          },
          runId
        );

        return { success: true };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        return { success: false, error: message };
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['runs', workflowName] });
        queryClient.invalidateQueries({ queryKey: ['infiniteRuns', workflowName] });
      }
    },
  });
};

// ============================================================================
// Public Hooks - Repetitions (for loops)
// ============================================================================

/**
 * Hook to get action repetitions (for forEach/Until loops)
 */
export const useActionRepetitions = (
  runId: string | null,
  scopeName: string | null,
  options: { enabled?: boolean } = {}
) => {
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();
  const { enabled = true } = options;

  return useInfiniteQuery({
    queryKey: ['actionRepetitions', runId, scopeName, workflowName],
    queryFn: async ({ pageParam }): Promise<RepetitionsPage> => {
      if (!projectName || !appName || !workflowName || !runId || !scopeName) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();

      if (pageParam) {
        return fetchMoreRepetitions(pageParam, token);
      }

      return fetchActionRepetitions(
        {
          projectName,
          appName,
          workflowName,
          accessToken: token,
        },
        runId,
        scopeName
      );
    },
    enabled: enabled && isReady && !!runId && !!scopeName,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextLink,
    staleTime: 60000,
  });
};

/**
 * Hook to get all repetitions flattened (convenience wrapper)
 */
export const useAllRepetitions = (
  runId: string | null,
  scopeName: string | null,
  options: { enabled?: boolean } = {}
) => {
  const query = useActionRepetitions(runId, scopeName, options);

  const repetitions = useMemo((): RepetitionInfo[] => {
    if (!query.data?.pages) return [];
    return query.data.pages.flatMap((page) => page.repetitions);
  }, [query.data?.pages]);

  return {
    ...query,
    data: repetitions,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
};

/**
 * Hook to get repetitions for a child action inside a scope (ForEach/Until).
 * Uses /actions/{actionName}/repetitions which works for both ForEach and Until children.
 */
export const useChildActionRepetitions = (
  runId: string | null,
  actionName: string | null,
  options: { enabled?: boolean } = {}
) => {
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();
  const { enabled = true } = options;

  const query = useQuery({
    queryKey: ['childActionRepetitions', runId, actionName, workflowName],
    queryFn: async (): Promise<RepetitionInfo[]> => {
      if (!projectName || !appName || !workflowName || !runId || !actionName) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();

      const result = await fetchChildActionRepetitions(
        {
          projectName,
          appName,
          workflowName,
          accessToken: token,
        },
        runId,
        actionName
      );

      return result.repetitions;
    },
    enabled: enabled && isReady && !!runId && !!actionName,
    staleTime: 60000,
  });

  return query;
};

/**
 * Hook to get details for a specific repetition
 */
export const useRepetitionDetails = (
  runId: string | null,
  scopeName: string | null,
  repetitionName: string | null
) => {
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();

  return useQuery({
    queryKey: ['repetitionDetails', runId, scopeName, repetitionName, workflowName],
    queryFn: async (): Promise<{ input?: unknown; output?: unknown }> => {
      if (!projectName || !appName || !workflowName || !runId || !scopeName || !repetitionName) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();

      const result = await fetchRepetitionDetails(
        {
          projectName,
          appName,
          workflowName,
          accessToken: token,
        },
        runId,
        scopeName,
        repetitionName
      );

      return result ?? {};
    },
    enabled: isReady && !!runId && !!scopeName && !!repetitionName,
    staleTime: 60000,
  });
};

/**
 * Hook that encapsulates all iteration (ForEach/Until) data fetching for an action.
 * Manages iteration selection state, fetches repetition lists and details,
 * and resets when the selected action changes.
 *
 * Used by both ActionContent (side panel) and LogsPanel (bottom panel).
 */
export function useIterationData(
  nodeExecution: NodeExecution | null | undefined,
  runId: string | null,
  options: {
    enabled?: boolean;
    nodeExecutions?: NodeExecution[] | undefined;
    /**
     * Externally-driven iteration selection. When the parent owns the active
     * repetition (e.g. LogsPanel iteration row click, canvas scrubber), pass
     * the repetition name here so the hook stays in sync. Local state is used
     * as a fallback when this is not provided.
     */
     externalRepetitionName?: string | null | undefined;
    /**
     * Notified whenever the user changes the iteration via this hook's
     * `setSelectedIndex` (e.g. clicking a card in IterationSelector). The
     * parent can lift selection so other surfaces (LogsPanel rows, canvas
     * scrubber) re-render to match.
     */
    onIterationChange?: ((repetitionName: string, index: number) => void) | undefined;
  } = {}
) {
  const { enabled = true, nodeExecutions, externalRepetitionName, onIterationChange } = options;
  const [internalSelectedIndex, setInternalSelectedIndex] = useState(0);

  const isAgent = nodeExecution ? isAgentExecution(nodeExecution) : false;
  const hasIterations = enabled && !isAgent && (nodeExecution?.iterationCount ?? 0) > 0;
  const isUntilScope = hasIterations && !nodeExecution?.parentNodeId
    && nodeExecution?.nodeType?.toLowerCase() === 'until';

  // For child actions inside ForEach/Until, use the child action's own name
  // to fetch repetitions via /actions/{childName}/repetitions.
  // This works for both ForEach and Until children.
  const childActionName = hasIterations && nodeExecution?.parentNodeId
    ? (nodeExecution?.nodeName ?? null)
    : null;

  // For Until scopes, find a child action to proxy iterations through.
  // The runtime doesn't support /scopeRepetitions for Until, but child actions
  // have /repetitions with one entry per loop iteration.
  const untilChildProxyName = useMemo(() => {
    if (!isUntilScope || !nodeExecutions) return null;
    const child = nodeExecutions.find(n => n.parentNodeId === nodeExecution?.nodeName);
    return child?.nodeName ?? null;
  }, [isUntilScope, nodeExecutions, nodeExecution?.nodeName]);

  // For ForEach scope actions, use scopeRepetitions endpoint
  const scopeName = hasIterations && !nodeExecution?.parentNodeId && !isUntilScope
    ? (nodeExecution?.nodeName ?? null)
    : null;

  // Use child action repetitions when this is a child of a scope (ForEach/Until)
  // OR when this is an Until scope (proxy through a child action)
  const childOrProxyName = childActionName ?? untilChildProxyName;
  const { data: childRepetitions, isLoading: isLoadingChildRepetitions } = useChildActionRepetitions(
    runId,
    childOrProxyName,
    { enabled: hasIterations && !!childOrProxyName && !!runId }
  );

  // Use scope repetitions when this is a ForEach scope action
  const { data: scopeRepetitionsList, isLoading: isLoadingScopeRepetitions } = useAllRepetitions(
    runId,
    scopeName,
    { enabled: hasIterations && !!scopeName && !!runId }
  );

  const repetitions = useMemo(
    () => childOrProxyName ? (childRepetitions ?? []) : scopeRepetitionsList,
    [childOrProxyName, childRepetitions, scopeRepetitionsList]
  );
  const isLoadingRepetitions = childOrProxyName ? isLoadingChildRepetitions : isLoadingScopeRepetitions;

  // Resolve the active iteration. If a parent passes `externalRepetitionName`
  // and we can find it in the loaded list, that wins (controlled mode).
  // Otherwise we fall back to the hook's internal index.
  const externalIndex = useMemo(() => {
    if (!externalRepetitionName || !repetitions || repetitions.length === 0) return null;
    const idx = repetitions.findIndex((r) => r.name === externalRepetitionName);
    return idx >= 0 ? idx : null;
  }, [externalRepetitionName, repetitions]);

  const rawSelectedIndex = externalIndex ?? internalSelectedIndex;
  // Clamp to valid range so that stale internal index or an external name that
  // doesn't match the current list can't drive an out-of-bounds access.
  const selectedIndex = repetitions && repetitions.length > 0
    ? Math.min(Math.max(0, rawSelectedIndex), repetitions.length - 1)
    : 0;

  // Wrap setSelectedIndex so iteration changes from this hook (e.g. the
  // IterationSelector cards) propagate up to whoever owns lifted state.
  const setSelectedIndex = useCallback(
    (index: number) => {
      setInternalSelectedIndex(index);
      const repName = repetitions?.[index]?.name;
      if (repName && onIterationChange) {
        onIterationChange(repName, index);
      }
    },
    [repetitions, onIterationChange]
  );

  const selectedRepetitionName = useMemo(() => {
    if (!repetitions || repetitions.length === 0) return null;
    const safeIndex = Math.min(selectedIndex, repetitions.length - 1);
    return repetitions[safeIndex]?.name ?? null;
  }, [repetitions, selectedIndex]);

  // For per-iteration content, the runtime exposes
  // `/actions/{name}/repetitions/{repId}` only for actions that have repetitions
  // of their own — child actions inside ForEach/Until, or Until scopes via a
  // child proxy. ForEach scope rows themselves don't have this route in Otto
  // (it 404s with ActionRepetitionNotFound), so we disable the fetch in that
  // case. The iteration list still renders from /scopeRepetitions; children
  // expose their own per-iteration data when the user drills into one.
  const detailsActionName = childActionName ?? untilChildProxyName ?? null;

  const { data: details, isLoading: isLoadingDetails } = useRepetitionDetails(
    runId,
    detailsActionName,
    selectedRepetitionName
  );

  // Reset iteration index when switching to a different action
  useEffect(() => {
    setInternalSelectedIndex(0);
  }, [nodeExecution?.nodeId]);

  return {
    hasIterations,
    repetitions,
    selectedIndex,
    setSelectedIndex,
    details,
    /** Whether the repetitions list is loading (for IterationSelector) */
    isLoading: hasIterations && isLoadingRepetitions,
    /** Whether repetition content (input/output) is loading (for content fade) */
    isLoadingContent: hasIterations && isLoadingDetails,
    /** Whether any iteration-related data is loading */
    isLoadingAny: hasIterations && (isLoadingRepetitions || isLoadingDetails),
  };
}

/**
 * Hook to get scope actions within a repetition
 *
 * `childNames` should be the list of action names that are children of the
 * scope (typically derived from `nodeExecutions.filter(e => e.parentNodeId === scopeName)`
 * in the caller). Otto's runtime exposes `/actions/{childName}/repetitions/{rep}` per
 * child rather than a single per-iteration scope endpoint, so we fan out per child.
 */
export const useRepetitionScopeActions = (
  runId: string | null,
  scopeName: string | null,
  repetitionName: string | null,
  childNames: string[] = []
) => {
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();

  // Stable cache key based on the sorted child names so re-orderings don't
  // invalidate the cache, but a different set does.
  const childKey = [...childNames].sort().join('|');

  return useQuery({
    queryKey: ['repetitionScopeActions', runId, scopeName, repetitionName, workflowName, childKey],
    queryFn: async (): Promise<NodeExecution[]> => {
      if (!projectName || !appName || !workflowName || !runId || !scopeName || !repetitionName) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();

      return fetchRepetitionScopeActions(
        {
          projectName,
          appName,
          workflowName,
          accessToken: token,
        },
        runId,
        scopeName,
        repetitionName,
        childNames
      );
    },
    enabled: isReady && !!runId && !!scopeName && !!repetitionName && childNames.length > 0,
    staleTime: 60000,
  });
};

// ============================================================================
// AGENT ITERATIONS
// ============================================================================

/**
 * Hook to get agent iterations for an agent action
 */
export const useAgentIterations = (
  runId: string | null,
  agentName: string | null,
  options: { enabled?: boolean } = {}
) => {
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();

  return useQuery({
    queryKey: ['agentIterations', runId, agentName, workflowName],
    queryFn: async (): Promise<AgentIterationInfo[]> => {
      if (!projectName || !appName || !workflowName || !runId || !agentName) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();

      return fetchAgentRepetitions(
        {
          projectName,
          appName,
          workflowName,
          accessToken: token,
        },
        runId,
        agentName
      );
    },
    enabled: isReady && !!runId && !!agentName && (options.enabled ?? true),
    staleTime: 30000, // Shorter stale time for active agent runs
  });
};

/**
 * Hook to get tool actions within an agent iteration.
 *
 * For built-in tools (code_interpreter, file_search), pass iterationInfo to include them
 * in the results. Built-in tools don't appear in the /actions endpoint, so their
 * input/output is fetched from the iteration-level content links.
 *
 * @param runId - The run ID
 * @param agentName - The agent action name
 * @param repetitionName - The iteration name (e.g., "000000")
 * @param iterationInfo - Optional iteration info for handling built-in tools
 * @param options - Query options
 */
export const useAgentIterationActions = (
  runId: string | null,
  agentName: string | null,
  repetitionName: string | null,
  iterationInfo?: BuiltInToolIterationInfo | null,
  options: { enabled?: boolean } = {}
) => {
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();

  return useQuery({
    queryKey: ['agentIterationActions', runId, agentName, repetitionName, workflowName, iterationInfo?.tools?.length],
    queryFn: async (): Promise<AgentToolActionInfo[]> => {
      if (!projectName || !appName || !workflowName || !runId || !agentName || !repetitionName) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();

      return fetchAgentRepetitionActions(
        {
          projectName,
          appName,
          workflowName,
          accessToken: token,
        },
        runId,
        agentName,
        repetitionName,
        iterationInfo ?? undefined
      );
    },
    enabled: isReady && !!runId && !!agentName && !!repetitionName && (options.enabled ?? true),
    staleTime: 60000,
  });
};

/**
 * Hook to get agent iterations for ALL agent nodes in a Stateful workflow.
 * Uses the agentRepetitions API for each agent (one call per agent).
 * Returns a Map of agentName -> AgentIterationInfo[]
 *
 * Use this for Stateful workflows with Agent actions.
 * For Agent kind (A2A) workflows, use useAllAgentIterations instead (uses Timeline API).
 */
export const useStatefulAgentIterations = (
  runId: string | null,
  agentNames: string[],
  options: { enabled?: boolean } = {}
) => {
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();
  const enabled = isReady && !!runId && agentNames.length > 0 && (options.enabled ?? true);

  const query = useQuery({
    queryKey: ['statefulAgentIterations', runId, workflowName, agentNames.join(',')],
    queryFn: async (): Promise<Map<string, AgentIterationInfo[]>> => {
      if (!projectName || !appName || !workflowName || !runId || agentNames.length === 0) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();
      const iterationsByAgent = new Map<string, AgentIterationInfo[]>();

      // Fetch iterations for each agent in parallel
      const results = await Promise.all(
        agentNames.map(async (agentName) => {
          try {
            const iterations = await fetchAgentRepetitions(
              {
                projectName,
                appName,
                workflowName,
                accessToken: token,
              },
              runId,
              agentName
            );
            return { agentName, iterations };
          } catch (error) {
            console.warn(`[useStatefulAgentIterations] Failed to fetch iterations for agent ${agentName}:`, error);
            return { agentName, iterations: [] };
          }
        })
      );

      // Build the map from results
      for (const { agentName, iterations } of results) {
        if (iterations.length > 0) {
          iterationsByAgent.set(agentName, iterations);
        }
      }

      return iterationsByAgent;
    },
    enabled,
    staleTime: 30000,
  });

  return {
    data: query.data ?? new Map<string, AgentIterationInfo[]>(),
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

/**
 * Hook to get agent iterations for ALL agent nodes in an Agent kind (A2A) workflow.
 * Uses the timeline API to fetch all iterations in a single call.
 * Returns a Map of agentName -> AgentIterationInfo[]
 *
 * Use this for Agent kind workflows (isConversational).
 * For Stateful workflows with Agent actions, use useStatefulAgentIterations instead.
 */
export const useAllAgentIterations = (
  runId: string | null,
  agentNames: string[], // Used as fallback when timeline API returns empty
  options: { enabled?: boolean } = {}
) => {
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();
  const enabled = isReady && !!runId && (options.enabled ?? true);

  const query = useQuery({
    queryKey: ['runTimeline', runId, workflowName, agentNames],
    queryFn: async (): Promise<Map<string, AgentIterationInfo[]>> => {
      if (!projectName || !appName || !workflowName || !runId) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();

      return fetchRunTimeline(
        {
          projectName,
          appName,
          workflowName,
          accessToken: token,
        },
        runId
      );
    },
    enabled,
    staleTime: 30000,
  });

  return {
    data: query.data ?? new Map<string, AgentIterationInfo[]>(),
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

// ============================================================================
// CHAT HISTORY
// ============================================================================

/**
 * Hook to get chat history for a run (A2A/Agent kind workflows)
 * Returns chat messages including user messages, assistant responses, and tool results
 */
export const useChatHistory = (
  runId: string | null,
  options: { enabled?: boolean } = {}
) => {
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();
  const enabled = isReady && !!runId && (options.enabled ?? true);

  return useQuery({
    queryKey: ['chatHistory', runId, workflowName],
    queryFn: async (): Promise<ChatMessageEntry[]> => {
      if (!projectName || !appName || !workflowName || !runId) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();

      return fetchRunChatHistory(
        {
          projectName,
          appName,
          workflowName,
          accessToken: token,
        },
        runId
      );
    },
    enabled,
    staleTime: 30000,
  });
};

/**
 * Hook to get chat history for a specific Agent action in a Stateful workflow.
 * This is used when viewing a Stateful workflow run that contains an Agent action.
 * The chat is displayed as read-only since it's historical data.
 *
 * @param runId - The workflow run ID
 * @param actionName - The name of the Agent action (e.g., "Agent")
 * @param options - Query options including enabled flag
 */
export const useActionChatHistory = (
  runId: string | null,
  actionName: string | null,
  options: { enabled?: boolean } = {}
) => {
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();
  const enabled = isReady && !!runId && !!actionName && (options.enabled ?? true);

  return useQuery({
    queryKey: ['actionChatHistory', runId, actionName, workflowName],
    queryFn: async (): Promise<ChatMessageEntry[]> => {
      if (!projectName || !appName || !workflowName || !runId || !actionName) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();

      return fetchActionChatHistory(
        {
          projectName,
          appName,
          workflowName,
          accessToken: token,
        },
        runId,
        actionName
      );
    },
    enabled,
    staleTime: 30000,
  });
};

/**
 * Hook to get chat history for all Agent actions in a Stateful workflow.
 * Fetches chat history from each agent in parallel and combines them sorted by timestamp.
 *
 * @param runId - The workflow run ID
 * @param actionNames - Array of agent action names
 * @param options - Query options including enabled flag
 */
export const useAllActionsChatHistory = (
  runId: string | null,
  actionNames: string[],
  options: { enabled?: boolean } = {}
) => {
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();
  const baseEnabled = isReady && !!runId && actionNames.length > 0 && (options.enabled ?? true);

  const queries = useQueries({
    queries: actionNames.map((actionName) => ({
      queryKey: ['actionChatHistory', runId, actionName, workflowName],
      queryFn: async (): Promise<{ actionName: string; messages: ChatMessageEntry[] }> => {
        if (!projectName || !appName || !workflowName || !runId) {
          throw new Error('Missing required context');
        }

        const token = await acquireToken();

        const messages = await fetchActionChatHistory(
          {
            projectName,
            appName,
            workflowName,
            accessToken: token,
          },
          runId,
          actionName
        );

        // Return both the action name and messages so we can inject the agent name
        return { actionName, messages };
      },
      enabled: baseEnabled,
      staleTime: 30000,
    })),
  });

  // Combine results: merge all chat histories, inject agent name if missing, and sort by timestamp
  const combinedData = useMemo(() => {
    const allMessages: ChatMessageEntry[] = [];
    for (const query of queries) {
      if (query.data) {
        const { actionName, messages } = query.data;
        // Inject agent name into messages that don't have it
        for (const msg of messages) {
          if (!msg.agentMetadata?.agentName) {
            msg.agentMetadata = {
              ...msg.agentMetadata,
              agentName: actionName,
            };
          }
          allMessages.push(msg);
        }
      }
    }
    // Sort by timestamp
    return allMessages.sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }, [queries]);

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);
  const error = queries.find((q) => q.error)?.error;

  return {
    data: combinedData.length > 0 ? combinedData : undefined,
    isLoading,
    isError,
    error,
  };
};

// ============================================================================
// CHAT MESSAGE ITERATION ACTIONS (OMT-based workflows)
// ============================================================================

/**
 * Hook to fetch actions for a specific chatMessage iteration.
 * 
 * This is designed for OMT-based chatMessage workflows where each message turn
 * can contain the full range of stateful workflow actions.
 * 
 * @param runId - The workflow run ID
 * @param iterationIndex - The 0-based iteration index (corresponds to message turn)
 * @param options - Query options
 */
export const useChatMessageIterationActions = (
  runId: string | null,
  iterationIndex: number | null,
  options: { enabled?: boolean } = {}
) => {
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();
  const enabled = isReady && !!runId && iterationIndex !== null && (options.enabled ?? true);

  return useQuery({
    queryKey: ['chatMessageIterationActions', runId, iterationIndex, workflowName],
    queryFn: async (): Promise<ChatMessageIterationAction[]> => {
      if (!projectName || !appName || !workflowName || !runId || iterationIndex === null) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();

      return fetchChatMessageIterationActions(
        {
          projectName,
          appName,
          workflowName,
          accessToken: token,
        },
        runId,
        iterationIndex
      );
    },
    enabled,
    staleTime: 30000,
  });
};

/**
 * Fetches chatMessage iteration actions as NodeExecution[] format.
 * 
 * This is the unified API for OMT per-message monitoring that returns
 * the EXACT same data structure as stateful workflow monitoring.
 * 
 * By returning NodeExecution[], we can reuse all stateful monitoring components
 * without any conversion or special-casing.
 * 
 * NOTE: This is ONLY for OMT chatMessage workflows (trigger: when_a_chat_message_is_received).
 * Non-OMT Agent workflows (kind: Agent with A2A) continue using the existing
 * useAgentIterations/useAgentIterationActions hooks.
 * 
 * @param runId - The workflow run ID
 * @param iterationIndex - The 0-based iteration index (message turn)
 * @param options - Query options
 * @returns Query result with NodeExecution[] data
 */
export const useChatMessageIterationNodeExecutions = (
  runId: string | null,
  iterationIndex: number | null,
  options: { enabled?: boolean } = {}
) => {
  const { workflowName, projectName, appName, acquireToken, isReady } = useMonitoringApiOptions();
  const enabled = isReady && !!runId && iterationIndex !== null && (options.enabled ?? true);

  return useQuery({
    queryKey: ['chatMessageIterationNodeExecutions', runId, iterationIndex, workflowName],
    queryFn: async (): Promise<NodeExecution[]> => {
      if (!projectName || !appName || !workflowName || !runId || iterationIndex === null) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();

      // Fetch the raw actions
      const actions = await fetchChatMessageIterationActions(
        {
          projectName,
          appName,
          workflowName,
          accessToken: token,
        },
        runId,
        iterationIndex
      );

      // Transform to NodeExecution[] - same format as stateful workflows
      const nodeExecutions: NodeExecution[] = actions.map((action) => {
        // Determine if this is a scope-type action
        const scopeTypes = ['if', 'switch', 'foreach', 'until', 'scope'];
        const isScope = scopeTypes.some(type => action.type.toLowerCase().includes(type));

        const nodeExecution: NodeExecution = {
          nodeId: `action-${action.name}`,
          nodeName: action.name,
          nodeType: action.type,
          status: action.status,
          startTime: action.startTime,
          endTime: action.endTime,
          duration: action.duration,
        };

        // Include error details if present
        if (action.error) {
          nodeExecution.error = {
            code: action.error.code,
            message: action.error.message,
          };
        }

        // Include iteration count for scope actions (If, Switch, ForEach, Until, Scope)
        if (isScope && action.childCount !== undefined) {
          nodeExecution.iterationCount = action.childCount;
        }

        return nodeExecution;
      });

      // Sort by start time (same as stateful)
      nodeExecutions.sort((a, b) => {
        if (!a.startTime) return 1;
        if (!b.startTime) return -1;
        return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
      });

      return nodeExecutions;
    },
    enabled,
    staleTime: 30000,
  });
};
